/**
 * Scale handle.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, KeyPoints, Point3D } from "../types";
import { BaseHandle } from "./BaseHandle";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
  HANDLE_OFFSET_PIXELS,
} from "../types";
import { distance2D } from "../matrix";

/**
 * Handle for uniform scaling entities.
 * Can be positioned at corners or at a custom offset position.
 */
export class ScaleHandle extends BaseHandle {
  readonly type = HandleType.Scale;
  readonly position: HandlePosition;

  /** The anchor point (opposite corner) for scaling */
  private anchorPoint: Point3D;

  /** scale handles does not snap to anything */
  snapOptions = { acceptOrtho: false, acceptPolar: false, acceptObject: false };

  /** Initial distance from anchor to handle */
  private initialDistance: number;

  private diagonalOffsetInPx =
    (HANDLE_OFFSET_PIXELS * HANDLE_SIZES.handleScale) / Math.sqrt(2);
  private diagonalWorldOffset: number;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
    position: HandlePosition,
  ) {
    super(viewer, history, entityIds, keyPoints, position);

    this.diagonalWorldOffset = this.viewer.screenDistanceToWorld(
      this.diagonalOffsetInPx,
    );

    this.position = position;

    this.worldPosition = {
      x: this.keyPoints.max.x + this.diagonalWorldOffset,
      y: this.keyPoints.min.y - this.diagonalWorldOffset,
      z: 0,
    };

    // Set anchor to opposite corner (topLeft for bottom-right positioned handle)
    this.anchorPoint = this.getOppositeCorner(position);

    // Calculate initial distance
    this.initialDistance = distance2D(this.anchorPoint, this.worldPosition);

    // Override draw options for scale handle
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.scaleHandle,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.scale * HANDLE_SIZES.handleScale,
      icon: "scale",
    };
  }

  /**
   * Get the opposite corner for anchoring.
   */
  private getOppositeCorner(position: HandlePosition): Point3D {
    switch (position) {
      case HandlePosition.TopLeft:
        return this.keyPoints.bottomRight;
      case HandlePosition.TopRight:
        return this.keyPoints.bottomLeft;
      case HandlePosition.BottomLeft:
        return this.keyPoints.topRight;
      case HandlePosition.BottomRight:
        return this.keyPoints.topLeft;
      default:
        return this.keyPoints.center;
    }
  }

  /**
   * Update ghost scale during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Calculate current distance from anchor to cursor
    const currentDistance = distance2D(this.anchorPoint, context.worldPoint);

    // Calculate scale ratio
    let scale = currentDistance / this.initialDistance;

    // Clamp scale to reasonable values
    scale = Math.max(0.1, Math.min(10, scale));

    // Snap to common scales if Shift is held
    if (context.shiftKey) {
      const snapValues = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0, 4.0];
      scale = snapValues.reduce((prev, curr) =>
        Math.abs(curr - scale) < Math.abs(prev - scale) ? curr : prev,
      );
    }

    // Update accumulated scale (uniform scaling sets all axes to same value)
    this.setAccumulated({
      scale: { x: scale, y: scale, z: scale },
      scaleCenter: { ...this.anchorPoint },
    });

    // Apply to all ghosts
    this.setGhostTransforms();

    // Update view with throttling for better performance
    this.throttledUpdate();
  }

  beforeRedraw(): void {
    const previousWorldDiagonalOffset = this.diagonalWorldOffset;
    this.diagonalWorldOffset = this.viewer.screenDistanceToWorld(
      this.diagonalOffsetInPx,
    );
    this.worldPosition.x +=
      this.diagonalWorldOffset - previousWorldDiagonalOffset;
    this.worldPosition.y +=
      -this.diagonalWorldOffset + previousWorldDiagonalOffset;
  }
}
